<script lang="ts">
	import CookieReport from "@components/CookieReport.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Heading from "@components/common/Heading.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import { getTranslate } from "@utils/getTranslate.js";
	import { initScrollSpy } from "@utils/initScrollSpy.js";
	import translations from "./Gdpr.translations.json" with { type: "json" };
	import type { GdprProps } from "./GdprProps.js";

	const _: GdprProps = $props();

	const translate = getTranslate(translations);

	const sections = [
		{ id: "intro", key: "sectionIntro" },
		{ id: "controller", key: "sectionController" },
		{ id: "data-processed", key: "sectionDataProcessed" },
		{ id: "purposes", key: "sectionPurposes" },
		{ id: "recipients", key: "sectionRecipients" },
		{ id: "rights", key: "sectionRights" },
		{ id: "contact", key: "sectionContact" },
		{ id: "cookies", key: "sectionCookies" },
		{ id: "cookie-types", key: "sectionCookieTypes" },
		{ id: "cookie-report", key: "sectionCookieReport" },
	] as const;

	let activeId: string = $state(sections[0].id);
</script>

<Layout>
	<div
		class="tos-layout container-10"
		use:initScrollSpy={{
			sections,
			setActiveId: (id): void => {
				activeId = id;
			},
		}}
	>
		<nav class="tos-nav">
			<div class="sticky top-28">
				<p class="text-xs font-semibold uppercase tracking-wider text-fcrdarkgray/40 mb-4 pl-4">Obsah</p>
				<ul class="text-sm leading-relaxed">
					{#each sections as section (section.id)}
						<li>
							<a
								href="#{section.id}"
								class="block py-1.5 transition-colors duration-200 border-l-2 pl-4 {activeId === section.id
									? 'border-fcrdarkblue text-fcrdarkblue font-semibold'
									: 'border-fcrdarkgray/10 text-fcrdarkgray/40 hover:text-fcrdarkgray/70 hover:border-fcrdarkgray/30'}"
							>
								<Formatted text={translate(section.key)} />
							</a>
						</li>
					{/each}
				</ul>
			</div>
		</nav>

		<section class="tos-content font-medium">
			<Heading level={1} text={translate("heading")} class="heading-1 mb-6" />
			<p class="mb-10 text-sm text-fcrdarkgray/50">
				<Formatted text={translate("effectiveDate")} />
			</p>

			<!-- 1. Intro -->
			<Heading level={2} text={translate("sectionIntro")} id="intro" class="heading-2 mt-16 mb-6" />
			<p class="mb-4"><Formatted text={translate("introText1")} /></p>
			<p class="mb-4"><Formatted text={translate("introText2")} /></p>

			<!-- 2. Controller -->
			<Heading level={2} text={translate("sectionController")} id="controller" class="heading-2 mt-16 mb-6" />
			<p class="mb-4"><Formatted text={translate("controllerText")} /></p>
			<ul class="list-disc pl-6 mb-4 space-y-1">
				<li><Formatted text={translate("controllerName")} /></li>
				<li><Formatted text={translate("controllerCin")} /></li>
				<li><Formatted text={translate("controllerAddress")} /></li>
				<li><Formatted text={translate("controllerEmail")} /></li>
				<li><Formatted text={translate("controllerPhone")} /></li>
			</ul>

			<!-- 3. Data processed -->
			<Heading level={2} text={translate("sectionDataProcessed")} id="data-processed" class="heading-2 mt-16 mb-6" />
			<p class="mb-4"><Formatted text={translate("dataProcessedIntro")} /></p>
			<ul class="list-disc pl-6 mb-4 space-y-2">
				<li><Formatted text={translate("dataProcessedIdentification")} /></li>
				<li><Formatted text={translate("dataProcessedContact")} /></li>
				<li><Formatted text={translate("dataProcessedDelivery")} /></li>
				<li><Formatted text={translate("dataProcessedOrder")} /></li>
				<li><Formatted text={translate("dataProcessedAccount")} /></li>
				<li><Formatted text={translate("dataProcessedBehavior")} /></li>
				<li><Formatted text={translate("dataProcessedMarketing")} /></li>
			</ul>

			<!-- 4. Purposes -->
			<Heading level={2} text={translate("sectionPurposes")} id="purposes" class="heading-2 mt-16 mb-6" />
			<p class="mb-4"><Formatted text={translate("purposesIntro")} /></p>

			<Heading level={3} text={translate("purposeOrderHeading")} class="font-antipol text-xl mt-8 mb-3" />
			<p class="mb-2"><Formatted text={translate("purposeOrderText")} /></p>
			<p class="mb-4 text-sm text-fcrdarkgray/70"><Formatted text={translate("purposeOrderBasis")} /></p>

			<Heading level={3} text={translate("purposeAccountHeading")} class="font-antipol text-xl mt-8 mb-3" />
			<p class="mb-2"><Formatted text={translate("purposeAccountText")} /></p>
			<p class="mb-4 text-sm text-fcrdarkgray/70"><Formatted text={translate("purposeAccountBasis")} /></p>

			<Heading level={3} text={translate("purposeLegalHeading")} class="font-antipol text-xl mt-8 mb-3" />
			<p class="mb-2"><Formatted text={translate("purposeLegalText")} /></p>
			<p class="mb-4 text-sm text-fcrdarkgray/70"><Formatted text={translate("purposeLegalBasis")} /></p>

			<Heading level={3} text={translate("purposeMarketingHeading")} class="font-antipol text-xl mt-8 mb-3" />
			<p class="mb-2"><Formatted text={translate("purposeMarketingText")} /></p>
			<p class="mb-4 text-sm text-fcrdarkgray/70"><Formatted text={translate("purposeMarketingBasis")} /></p>

			<Heading level={3} text={translate("purposeAnalyticsHeading")} class="font-antipol text-xl mt-8 mb-3" />
			<p class="mb-2"><Formatted text={translate("purposeAnalyticsText")} /></p>
			<p class="mb-4 text-sm text-fcrdarkgray/70"><Formatted text={translate("purposeAnalyticsBasis")} /></p>

			<!-- 5. Recipients -->
			<Heading level={2} text={translate("sectionRecipients")} id="recipients" class="heading-2 mt-16 mb-6" />
			<p class="mb-4"><Formatted text={translate("recipientsIntro")} /></p>
			<ul class="list-disc pl-6 mb-4 space-y-2">
				<li><Formatted text={translate("recipientFakturoid")} /></li>
				<li><Formatted text={translate("recipientGoPay")} /></li>
				<li><Formatted text={translate("recipientPpl")} /></li>
				<li><Formatted text={translate("recipientGoogle")} /></li>
				<li><Formatted text={translate("recipientMeta")} /></li>
				<li><Formatted text={translate("recipientHotjar")} /></li>
				<li><Formatted text={translate("recipientAws")} /></li>
			</ul>
			<p class="mb-4"><Formatted text={translate("recipientsNote")} /></p>

			<!-- 6. Rights -->
			<Heading level={2} text={translate("sectionRights")} id="rights" class="heading-2 mt-16 mb-6" />
			<p class="mb-4"><Formatted text={translate("rightsIntro")} /></p>
			<ul class="list-disc pl-6 mb-4 space-y-2">
				<li><Formatted text={translate("rightAccess")} /></li>
				<li><Formatted text={translate("rightRectification")} /></li>
				<li><Formatted text={translate("rightErasure")} /></li>
				<li><Formatted text={translate("rightRestriction")} /></li>
				<li><Formatted text={translate("rightPortability")} /></li>
				<li><Formatted text={translate("rightObject")} /></li>
				<li><Formatted text={translate("rightWithdrawConsent")} /></li>
				<li><Formatted text={translate("rightComplaint")} /></li>
			</ul>
			<p class="mb-4"><Formatted text={translate("rightsRetention")} /></p>

			<!-- 7. Contact -->
			<Heading level={2} text={translate("sectionContact")} id="contact" class="heading-2 mt-16 mb-6" />
			<p class="mb-4"><Formatted text={translate("contactText")} /></p>
			<ul class="list-disc pl-6 mb-4 space-y-1">
				<li><Formatted text={translate("contactEmail")} /></li>
				<li><Formatted text={translate("contactPostal")} /></li>
			</ul>
			<p class="mb-4"><Formatted text={translate("contactUoou")} /></p>

			<!-- 8. Cookies -->
			<Heading level={2} text={translate("sectionCookies")} id="cookies" class="heading-2 mt-16 mb-6" />
			<p class="mb-4"><Formatted text={translate("cookiesIntro")} /></p>
			<p class="mb-4"><Formatted text={translate("cookiesConsent")} /></p>

			<!-- 9. Cookie types -->
			<Heading level={2} text={translate("sectionCookieTypes")} id="cookie-types" class="heading-2 mt-16 mb-6" />

			<Heading level={3} text={translate("cookieEssentialHeading")} class="font-antipol text-xl mt-8 mb-3" />
			<p class="mb-4"><Formatted text={translate("cookieEssentialText")} /></p>

			<Heading level={3} text={translate("cookieAnalyticsHeading")} class="font-antipol text-xl mt-8 mb-3" />
			<p class="mb-4"><Formatted text={translate("cookieAnalyticsText")} /></p>

			<Heading level={3} text={translate("cookieMarketingHeading")} class="font-antipol text-xl mt-8 mb-3" />
			<p class="mb-4"><Formatted text={translate("cookieMarketingText")} /></p>

			<!-- 10. Cookie report -->
			<Heading level={2} text={translate("sectionCookieReport")} id="cookie-report" class="heading-2 mt-16 mb-6" />
			<p class="mb-6"><Formatted text={translate("cookieReportIntro")} /></p>
			<CookieReport />
		</section>
	</div>
</Layout>

<style lang="postcss">
	.tos-layout {
		@apply flex gap-12 py-10 lg:gap-20 lg:py-16;
	}

	.tos-nav {
		@apply hidden shrink-0 lg:block;
		width: 240px;
	}

	.tos-content :global {
		p,
		ul,
		ol,
		h1,
		h2,
		h3 {
			@apply min-w-0 text-base leading-relaxed;
			max-width: min(55ch, calc(100vw - var(--container-gap) * 2));
		}
	}

	.tos-content :global(h2) {
		scroll-margin-top: 7rem;
	}

	.tos-content :global(h3) {
		scroll-margin-top: 7rem;
	}
</style>
